import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MuiToggle from '@brands/Components/Inputs/MuiToggle/MuiToggle';
import { FollowUp } from '@brands/Dashboard/ConsultationPage/assets/types';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import {
  careCoordinationTypeOptions,
  careCoordinationUrgencyOptions,
  Option,
  oralHealthCoachingTypeOptions,
} from '@brands/Utils/selectOptions'; // removed alias to avoid confusion
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import React, { useEffect, useMemo } from 'react';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';

export type FollowUpFormData = {
  careCoordination: boolean | null;
  careType: string;
  careUrgency: string;
  careDescription: string;
  oralCoaching: boolean | null;
  oralType: string;
  oralUrgency: string;
  oralDescription: string;
};

type FollowUpModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: FollowUpFormData) => void;
  initialData?: FollowUp | null;
};

const emptyForm: FollowUpFormData = {
  careCoordination: null,
  careType: '',
  careUrgency: '',
  careDescription: '',
  oralCoaching: null,
  oralType: '',
  oralUrgency: '',
  oralDescription: '',
};

const FollowUpModal: React.FC<FollowUpModalProps> = ({ open, onClose, onSave, initialData }) => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);

  const [form, setForm] = React.useState<FollowUpFormData>(emptyForm);
  const [isOtherSelectedCare, setIsOtherSelectedCare] = React.useState(false);
  const [isOtherSelectedOral, setIsOtherSelectedOral] = React.useState(false);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      setForm({
        careCoordination: initialData.care_coordination ?? null,
        careType: initialData.care_coordination_type || '',
        careUrgency: initialData.care_coordination_urgency || '',
        careDescription: initialData.care_coordination_reason || '',
        oralCoaching: initialData.oral_health_coaching ?? null,
        oralType: initialData.oral_health_coaching_type || '',
        oralUrgency: initialData.oral_health_coaching_urgency || '',
        oralDescription: initialData.oral_health_coaching_reason || '',
      });
      setIsOtherSelectedCare(
        initialData.care_coordination_type === 'other' ||
          (!!initialData.care_coordination_type &&
            !careCoordinationTypeOptions.some((o) => o.value === initialData.care_coordination_type)),
      );
      setIsOtherSelectedOral(
        initialData.oral_health_coaching_type === 'other' ||
          (!!initialData.oral_health_coaching_type &&
            !oralHealthCoachingTypeOptions.some((o) => o.value === initialData.oral_health_coaching_type)),
      );
    } else {
      setForm(emptyForm);
      setIsOtherSelectedCare(false);
      setIsOtherSelectedOral(false);
    }
  }, [open, initialData]);

  const handleToggleChange = (section: 'care' | 'oral', value: string | null) => {
    if (value === null) return;
    const isYes = value === 'yes';
    setForm((prev) => ({
      ...prev,
      [section === 'care' ? 'careCoordination' : 'oralCoaching']: isYes,
    }));
  };

  const isFormValid = () => {
    const bothQuestionsAnswered = form.careCoordination !== null && form.oralCoaching !== null;

    if (!bothQuestionsAnswered) return false;

    if (form.careCoordination === true) {
      if (!form.careType || form.careType === 'other' || !form.careUrgency || !form.careDescription.trim()) {
        return false;
      }
    }

    if (form.oralCoaching === true) {
      if (!form.oralType || form.oralType === 'other' || !form.oralUrgency || !form.oralDescription.trim()) {
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
      return;
    }

    onSave(form);
    onClose();
  };

  const mobileCustomStylesDashboard: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard?.overlay || {}),
      overflowY: 'scroll' as const,
      overflowX: 'hidden' as const,
    },
    content: {
      ...(customStylesDashboard?.content || {}),
      width: '100vw',
      height: '100vh',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      right: '0',
      bottom: '0',
      padding: '90px 16px 32px 16px',
      margin: '0',
      borderRadius: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      overflowY: 'scroll' as const,
    },
  };

  const desktopCustomStyles: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...customStylesDashboard.overlay,
    },
    content: {
      ...customStylesDashboard.content,
      width: '444px',
      padding: '30px 32px 32px',
      flexDirection: 'column',
      overflowY: 'auto',
      maxHeight: '750px',
      justifyContent: 'flex-start',
    },
  };

  const title = initialData ? 'Edit follow-up care' : 'Add follow-up care';

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={isMobile ? mobileCustomStylesDashboard : desktopCustomStyles}
      key={initialData ? 'edit-followup' : 'add-followup'}
    >
      <div className={`${styles.body} ${styles.formContainer}`}>
        <div className={styles.followupModal}>
          <div className={styles.bodyHead}>
            <CloseIcon onClick={onClose} />
          </div>

          <div className={`${styles.body} ${styles.formContainer}`}>
            <span className={styles.bodyHeader}>{title}</span>

            {/* Care Coordination Section */}
            <div className={styles.bodySub}>
              <div className={styles.bodySubTitleDiv}>
                <span className={styles.bodySubTitle}>
                  Recommend Dentistry.One Care Coordination? <span className={styles.required}>*</span>
                </span>
                <span className={styles.bodySubTitleText}>
                  If you think the patient needs support with in-person follow-up care, you can recommend care
                  coordination.
                </span>
              </div>
              <MuiToggle
                label=""
                hideLabel
                data-testid="follow-up-care-coordination"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                required
                selectedValue={form.careCoordination === null ? '' : form.careCoordination ? 'yes' : 'no'}
                onChange={(val) => handleToggleChange('care', val)}
              />
            </div>

            {form.careCoordination && (
              <div className={styles.followUpFields}>
                <div className={styles.followUpFieldsRow}>
                  <MUISelectInput
                    label="Type"
                    value={
                      careCoordinationTypeOptions.find((o) => o.value === form.careType)
                        ? form.careType
                        : form.careType.trim() === ''
                        ? null
                        : 'other'
                    }
                    name="careType"
                    onChange={(e: any) => {
                      setForm({ ...form, careType: e });
                      setIsOtherSelectedCare(e === 'other');
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                    options={careCoordinationTypeOptions}
                  />

                  <MUISelectInput
                    label="Urgency"
                    value={careCoordinationUrgencyOptions.find((o) => o.value === form.careUrgency)?.value || null}
                    name="careUrgency"
                    onChange={(e: any) =>
                      setForm({
                        ...form,
                        careUrgency: e,
                      })
                    }
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                    options={careCoordinationUrgencyOptions}
                  />
                </div>

                {(isOtherSelectedCare ||
                  (!careCoordinationTypeOptions.some((o: Option) => o.value === form.careType) &&
                    form.careType.trim() !== '')) && (
                  <TextField
                    name="care-coordination-other-type"
                    value={form.careType !== 'other' ? form.careType : ''}
                    label="Other follow-up care"
                    required
                    onChange={(e) => setForm((p) => ({ ...p, careType: e.target.value }))}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                    inputProps={{ maxLength: undefined }}
                  />
                )}

                <TextField
                  multiline
                  minRows={4}
                  name="follow-up-care-coordination-description"
                  value={form.careDescription || ''}
                  label="Description"
                  required
                  onChange={(e) => setForm((p) => ({ ...p, careDescription: e.target.value }))}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                  inputProps={{ maxLength: undefined }}
                />
              </div>
            )}

            {/* Oral Health Coaching Section */}
            <div className={styles.bodySub} style={{ marginTop: '24px' }}>
              <div className={styles.bodySubTitleDiv}>
                <span className={styles.bodySubTitle}>
                  Recommend Dentistry.One Oral Health Coaching? <span className={styles.required}>*</span>
                </span>
                <span className={styles.bodySubTitleText}>
                  If you think the patient would benefit from speaking with a Dentistry.One oral health coach, you can
                  recommend Oral Health Coaching.
                </span>
              </div>
              <MuiToggle
                label=""
                hideLabel
                data-testid="follow-up-oral-coaching"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                required
                selectedValue={form.oralCoaching === null ? '' : form.oralCoaching ? 'yes' : 'no'}
                onChange={(val) => handleToggleChange('oral', val)}
              />

              {form.oralCoaching && (
                <div className={styles.followUpFields} style={{ marginTop: '16px' }}>
                  <div className={styles.followUpFieldsRow}>
                    <MUISelectInput
                      label="Type"
                      value={
                        oralHealthCoachingTypeOptions.find((o) => o.value === form.oralType)
                          ? form.oralType
                          : form.oralType.trim() === ''
                          ? null
                          : 'other'
                      }
                      name="oralType"
                      onChange={(e: any) => {
                        setForm({ ...form, oralType: e });
                        setIsOtherSelectedOral(e === 'other');
                      }}
                      required
                      inputClass={`${styles.profileInputClass} fs-exclude`}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                      options={oralHealthCoachingTypeOptions}
                    />

                    <MUISelectInput
                      label="Urgency"
                      value={careCoordinationUrgencyOptions.find((o) => o.value === form.oralUrgency)?.value || null}
                      name="oralUrgency"
                      onChange={(e: any) =>
                        setForm({
                          ...form,
                          oralUrgency: e,
                        })
                      }
                      required
                      inputClass={`${styles.profileInputClass} fs-exclude`}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                      options={careCoordinationUrgencyOptions}
                    />
                  </div>

                  {(isOtherSelectedOral ||
                    (!oralHealthCoachingTypeOptions.some((o: Option) => o.value === form.oralType) &&
                      form.oralType.trim() !== '')) && (
                    <TextField
                      name="oral-coaching-other-type"
                      value={form.oralType !== 'other' ? form.oralType : ''}
                      label="Other follow-up care"
                      required
                      onChange={(e) => setForm((p) => ({ ...p, oralType: e.target.value }))}
                      inputClass={`${styles.profileInputClass} fs-exclude`}
                      wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                      inputProps={{ maxLength: undefined }}
                    />
                  )}

                  <TextField
                    multiline
                    minRows={4}
                    name="follow-up-oral-coaching-description"
                    value={form.oralDescription || ''}
                    label="Description"
                    required
                    onChange={(e) => setForm((p) => ({ ...p, oralDescription: e.target.value }))}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                    inputProps={{ maxLength: undefined }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.bodyFooter}>
            <Button variant="contained" className={styles.containedBtn} onClick={handleSave} disabled={!isFormValid()}>
              {initialData ? 'Save changes' : 'Add follow-up care'}
            </Button>

            <Button variant="text" className={styles.textBtn} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FollowUpModal;
